/**
 * The browser's ONE entry point for offering catalog jobs as a NEW selection
 * (field test 2026-10-16, Wave 2 — catalog retirement).
 *
 * Rule:
 *   - NEW selection (category / submenu / job pickers, Quick Search, AI-intake
 *     and EVE matchers, import mapping, configuration pickers)
 *       → `newJobCatalogQuery(select)` — appends `retired_at IS NULL`.
 *   - LOOKUP by a stored id or code (existing jobs, canned-line rehydration,
 *     add-on dedupe labels, learning, metrics, bay snapshots)
 *       → plain `supabase.from('jobs_catalog')`, unfiltered.
 *
 * The census pin `tests/unit/catalog/newJobCatalogReaderCensus.test.ts` fails
 * on any new raw `from('jobs_catalog')` reader that is not classified.
 */
import { supabase } from '@/integrations/supabase/client';
import {
  CATALOG_RETIRED_COLUMN,
  isOfferableCatalogJob,
  isRetiredCatalogJob,
  offerableCatalogJobs,
  resolveSuccessor,
  type RetirableCatalogRow,
} from '../../../supabase/functions/_shared/catalog/catalogRetirement';
import { fetchAllPages, type FetchAllOptions, type PageError } from './catalogPagination';

export { isOfferableCatalogJob, isRetiredCatalogJob, offerableCatalogJobs, resolveSuccessor };
export type { RetirableCatalogRow };

/** Base query for every NEW-selection reader: `select(columns)` + `retired_at IS NULL`. */
export function newJobCatalogQuery<S extends string = '*'>(columns?: S, options?: { count?: 'exact' }) {
  return supabase
    .from('jobs_catalog')
    .select((columns ?? '*') as S, options)
    .is(CATALOG_RETIRED_COLUMN, null);
}

/**
 * EVERY live catalog row for a NEW-selection picker (Quick Search lists, the
 * category list), read page by page so PostgREST's silent max_rows cap (1000)
 * can never truncate the picker (audit D AD-8). Same retirement rule as
 * `newJobCatalogQuery`. `orderBy` columns ascending, then `id` as the
 * tie-break so pages never overlap or skip (labels are not unique).
 */
export async function fetchAllNewJobCatalogRows<T = any>(
  columns: string,
  orderBy: ReadonlyArray<string> = ['label'],
  options?: FetchAllOptions,
): Promise<{ data: T[] | null; error: PageError }> {
  return fetchAllPages<T>((from, to) => {
    let q = newJobCatalogQuery(columns, { count: 'exact' });
    for (const col of orderBy) q = q.order(col, { ascending: true });
    return q.order('id', { ascending: true }).range(from, to) as unknown as PromiseLike<{ data: T[] | null; error: PageError; count: number | null }>;
  }, options);
}

/**
 * Configuration pickers (add-on settings, OptiQueue allowed jobs): the live
 * rows, PLUS any currently-configured id even when it is retired, so the
 * existing setting is not shown blank. Retired configured rows are tagged
 * `retired: true` for a badge. Never offers a retired row that is not already
 * configured.
 */
export async function fetchConfigPickerJobs<T extends { id: string } & RetirableCatalogRow>(
  columns: string,
  keepIds: ReadonlyArray<string | null | undefined>,
): Promise<{ data: Array<T & { retired: boolean }>; error: unknown }> {
  const cols = columns.includes(CATALOG_RETIRED_COLUMN) ? columns : `${columns}, retired_at, superseded_by_code`;
  const live = await supabase.from('jobs_catalog').select(cols).is(CATALOG_RETIRED_COLUMN, null).order('label');
  if (live.error) return { data: [], error: live.error };
  const liveRows = (live.data ?? []) as unknown as T[];
  const have = new Set(liveRows.map((r) => r.id));
  const missing = keepIds.filter((id): id is string => !!id && !have.has(id));
  let kept: T[] = [];
  if (missing.length > 0) {
    // LOOKUP by configured id: resolves retired rows on purpose.
    const extra = await supabase.from('jobs_catalog').select(cols).in('id', missing);
    if (!extra.error) kept = (extra.data ?? []) as unknown as T[];
  }
  return {
    data: [
      ...liveRows.map((r) => ({ ...r, retired: false })),
      ...kept.map((r) => ({ ...r, retired: isRetiredCatalogJob(r) })),
    ],
    error: null,
  };
}

/** Display suffix for a retired row in a configuration picker or editor badge. */
export function retiredCatalogSuffix(row: RetirableCatalogRow | null | undefined): string {
  if (!isRetiredCatalogJob(row)) return '';
  return row?.superseded_by_code ? ` (retired — replaced by ${row.superseded_by_code})` : ' (retired)';
}
